// src/auth/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";

const AuthContext = createContext();

// Safe localStorage wrappers — gracefully handle sandboxed/private contexts
const lsGet = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
const lsSet = (key, val) => { try { localStorage.setItem(key, val); } catch { /* ignore */ } };
const lsRemove = (key) => { try { localStorage.removeItem(key); } catch { /* ignore */ } };

export function AuthProvider({ children }) {
    const [token, setToken] = useState(() => lsGet("token"));
    const [user, setUser] = useState(() => {
        const raw = lsGet("user");
        return raw ? JSON.parse(raw) : null;
    });
    const [isValid, setIsValid] = useState(true);

    /* ✅ ฟังก์ชันตรวจสอบ JWT หมดอายุ */
    const isTokenExpired = (tokenValue) => {
        try {
            const [, payloadBase64] = tokenValue.split(".");
            const payload = JSON.parse(atob(payloadBase64));
            if (!payload.exp) return false; // ถ้าไม่มี exp ถือว่ายังใช้ได้
            const now = Date.now() / 1000;
            return payload.exp < now;
        } catch (err) {
            console.warn("Invalid token format", err);
            return true; // ถ้า decode ไม่ได้ ถือว่า invalid
        }
    };

    const isLoggedIn = !!token && isValid;

    /* ✅ เมื่อ login ใหม่ (บันทึก token และ user) */
    const login = (tokenValue, userData = null) => {
        lsSet("token", tokenValue);
        if (userData) lsSet("user", JSON.stringify(userData));
        setToken(tokenValue);
        setUser(userData);
        setIsValid(true);
    };

    /* ✅ logout: ลบข้อมูลทั้งหมด */
    const logout = () => {
        lsRemove("token");
        lsRemove("user");
        setToken(null);
        setUser(null);
        setIsValid(false);
    };

    /* ✅ fetch พร้อมแนบ Authorization header อัตโนมัติ */
    const authFetch = async (url, options = {}) => {
        if (!token) throw new Error("No token found");
        if (isTokenExpired(token)) {
            logout();
            throw new Error("Token expired");
        }

        const headers = new Headers(options.headers || {});
        headers.set("Authorization", `Bearer ${token}`);
        return fetch(url, { ...options, headers });
    };

    /* ✅ ตรวจสอบ token เมื่อโหลดหน้า */
    useEffect(() => {
        if (token) {
            if (isTokenExpired(token)) {
                console.log("Token expired → auto logout");
                logout();
            } else {
                setIsValid(true);
            }
        }
    }, [token]);

    /* ✅ sync ระหว่างหลายแท็บ */
    useEffect(() => {
        const handleStorage = () => {
            const newToken = lsGet("token");
            const newUser = lsGet("user");
            if (newToken && !isTokenExpired(newToken)) {
                setToken(newToken);
                setUser(newUser ? JSON.parse(newUser) : null);
            } else {
                logout();
            }
        };
        window.addEventListener("storage", handleStorage);
        return () => window.removeEventListener("storage", handleStorage);
    }, []);

    return (
        <AuthContext.Provider value={{ isLoggedIn, token, user, login, logout, authFetch }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
